import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { Phone, Mail, MapPin, Users, Sparkles, Star, ExternalLink, UserRound } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { connectedThroughPerson } from '../../lib/hwmPresentation';

function fullName(c) {
  return [c.first_name, c.last_name].filter(Boolean).join(' ')
    || c.work?.company || c.organization || 'Unnamed contact';
}

function InfoCard({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="bg-muted/50 border border-border rounded-xl p-3">
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Icon size={13} />
        <span className="text-[11px] font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-sm font-medium text-foreground mt-1 break-words">{value}</p>
    </div>
  );
}

/**
 * Compact read-only contact summary shown in the RIGHT pane of the desktop
 * master–detail split (SearchPage ≥1024px). Renders straight from the
 * already-loaded contact object — no fetch. "Open full profile" jumps to the
 * full editable ContactDetailPage.
 */
export default function ContactDetailPane({ contact, onOpenFull, groups = [] }) {
  const { user } = useAuth();
  if (!contact) {
    return (
      <div className="h-full min-h-[320px] flex flex-col items-center justify-center text-center text-muted-foreground border border-dashed border-border rounded-2xl p-10" data-testid="detail-pane-empty">
        <UserRound size={40} className="mb-3 opacity-40" />
        <p className="text-sm font-medium">Select a contact to see their details here.</p>
      </div>
    );
  }

  const initials = `${(contact.first_name || '')[0] || ''}${(contact.last_name || '')[0] || ''}`.toUpperCase();
  const imageUrl = contact.image_url?.startsWith('/api')
    ? `${process.env.REACT_APP_BACKEND_URL}${contact.image_url}`
    : contact.image_url;
  const role = [contact.work?.title, contact.work?.company || contact.company, contact.addresses?.[0]?.city]
    .filter(Boolean).join(' · ');
  const phone = contact.phones?.[0]?.number;
  const email = contact.emails?.[0]?.email;
  const hwm = contact.how_we_met;
  const through = connectedThroughPerson(contact, user);
  const howWeMet = hwm && (hwm.description || hwm.at_location || through || hwm.date)
    ? [hwm.at_location, hwm.description, through && `via ${through}`, hwm.date]
        .filter(Boolean).join(' · ')
    : null;
  // Resolve the contact's group-membership IDs to readable names via the
  // already-loaded, authorized groups passed from SearchPage — never render raw
  // UUIDs. Hidden groups are filtered out (parity with the full profile), and
  // an unknown/not-yet-loaded ID is simply dropped, so the card shows only real
  // names (or nothing while groups are still loading).
  const groupNames = (contact.groups || [])
    .map((id) => (groups || []).find((g) => g.id === id))
    .filter((g) => g && !g.hidden)
    .map((g) => g.name)
    .filter(Boolean)
    .join(', ');

  return (
    <div className="bg-card border border-border rounded-2xl p-6 shadow-sm" data-testid="contact-detail-pane">
      <div className="flex items-start gap-4">
        <Avatar className={`h-20 w-20 ${contact.is_favorite ? 'avatar-ring' : 'ring-2 ring-border'}`}>
          <AvatarImage src={imageUrl} alt={fullName(contact)} className="object-cover" />
          <AvatarFallback className="bg-nav-blue text-white text-2xl font-heading font-bold">{initials}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-heading font-bold text-foreground leading-tight break-words">
              {fullName(contact)}
            </h2>
            {contact.is_favorite && <Star size={18} className="text-signal-yellow shrink-0" fill="currentColor" />}
          </div>
          {role && <p className="text-sm text-muted-foreground mt-1">{role}</p>}
          <Button
            onClick={onOpenFull}
            className="mt-3 h-9 bg-nav-blue hover:bg-nav-blue/90 text-white rounded-lg text-sm"
            data-testid="detail-pane-open-full"
          >
            Open full profile <ExternalLink size={14} className="ml-1.5" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-6">
        <InfoCard icon={Phone} label="Phone" value={phone} />
        <InfoCard icon={Mail} label="Email" value={email} />
        <InfoCard icon={Sparkles} label="How we met" value={howWeMet} />
        <InfoCard icon={MapPin} label="City" value={contact.addresses?.[0]?.city} />
        {groupNames && (
          <div className="col-span-2">
            <InfoCard icon={Users} label="Groups" value={groupNames} />
          </div>
        )}
        {contact.notes && (
          <div className="col-span-2 bg-muted/50 border border-border rounded-xl p-3">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Notes</span>
            <p className="text-sm text-foreground mt-1 whitespace-pre-wrap break-words line-clamp-6">{contact.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}
